import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { CustomerBillingAggregate, CustomerResourceCharge } from '../types/billing';
import {
  formatBillingCurrency,
  formatBillingHoursDisplay,
  sumAndRoundHalfUp2,
} from './billingRound';
import { renderPatchingPdfSections } from './patchingBillingPdfSection';
import { isResourceChargeExportRow } from '../constants/patchingGroups';
import { TENARAI_LOGO_BASE64 } from './tenaraiLogoBase64';

const formatCurrency = formatBillingCurrency;
const formatHours = formatBillingHoursDisplay;

// Get billing period date range
const getBillingPeriodDates = (billingPeriod: string) => {
  const [year, month] = billingPeriod.split('-').map(Number);
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };
  return {
    fromDate: formatDate(firstDay),
    toDate: formatDate(lastDay),
    monthYear: firstDay.toLocaleDateString('en-US', { year: 'numeric', month: 'long' }),
  };
};

// Format filename: Customer-Billing-customerName-monthName-Year
const formatCustomerBillingFilename = (customerName: string, billingPeriod: string, extension: string) => {
  const [year, month] = billingPeriod.split('-').map(Number);
  const date = new Date(year, month - 1, 1);
  const monthName = date.toLocaleDateString('en-US', { month: 'long' });
  const sanitizedCustomerName = customerName.replace(/[^a-zA-Z0-9]/g, '_');
  return `${sanitizedCustomerName}-Customer-Billing-${monthName}-${year}.${extension}`;
};

/** Shared 6-column layout so Fixed Hours and Overage Charges tables align in the PDF. */
function getFixedHoursBreakdownColumnStyles(tableWidth: number) {
  const engagementWidth = 34;
  const resourceWidth = 24;
  const hoursWidth = 22;
  const rateWidth = 22;
  const amountWidth = 24;
  const groupWidth =
    tableWidth - engagementWidth - resourceWidth - hoursWidth - rateWidth - amountWidth;
  return {
    0: { halign: 'left' as const, cellWidth: engagementWidth },
    1: { halign: 'left' as const, cellWidth: resourceWidth },
    2: { halign: 'left' as const, cellWidth: groupWidth },
    3: { halign: 'right' as const, cellWidth: hoursWidth },
    4: { halign: 'right' as const, cellWidth: rateWidth },
    5: { halign: 'right' as const, fontStyle: 'bold' as const, cellWidth: amountWidth },
  };
}

interface CustomerBillingPdfData {
  aggregate: CustomerBillingAggregate;
}

export const generateCustomerBillingPdf = (data: CustomerBillingPdfData): void => {
  const { aggregate } = data;
  const { fromDate, toDate, monthYear } = getBillingPeriodDates(aggregate.billingPeriod);
  const currency = aggregate.currency || 'USD';

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const tableWidth = pageWidth - 2 * margin; // Full width between margins
  const footerAreaHeight = 35; // Reserve space for footer at bottom (increased to prevent overlap)
  const maxContentY = pageHeight - footerAreaHeight; // Maximum Y position for content
  let yPosition = 20;
  const pulseLineY = yPosition;

  // Helper function to ensure content doesn't overlap with footer
  const ensureSpaceForFooter = (requiredHeight: number = 0) => {
    const currentY = yPosition;
    const availableSpace = maxContentY - currentY;
    if (availableSpace < requiredHeight + 10) {
      // Not enough space, add new page
      doc.addPage();
      yPosition = margin; // Reset to top of new page
      return true; // Page was added
    }
    return false; // No page break needed
  };

  // Header background - 100% width
  const headerHeight = 50;
  doc.setFillColor(249, 250, 251); // Light gray background
  doc.rect(0, 0, pageWidth, headerHeight, 'F');

  // Header - "Pulse" and tagline on left side
  // "Pulse" subtitle
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bolditalic');
  doc.setTextColor(31, 41, 55);
  doc.text('Pulse', margin, yPosition);

  // Tagline
  yPosition += 6;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(107, 114, 128);
  doc.text('Intelligent Cloud Monitoring & Remediation Platform', margin, yPosition);

  // Header - Tenarai branding on right, text aligned with Pulse on same line
  const tenaraiLogoSize = 16;
  const tenaraiBlockRight = pageWidth - margin;
  doc.setFontSize(22);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(75, 85, 99);
  const tenaraiText = 'Tenarai';
  const tenaraiTextWidth = doc.getTextWidth(tenaraiText);
  doc.text(tenaraiText, tenaraiBlockRight - tenaraiTextWidth, pulseLineY);

  try {
    const tenaraiLogoX = tenaraiBlockRight - tenaraiTextWidth + (tenaraiTextWidth - tenaraiLogoSize) / 2;
    doc.addImage(
      TENARAI_LOGO_BASE64,
      'PNG',
      tenaraiLogoX,
      pulseLineY - 2,
      tenaraiLogoSize,
      tenaraiLogoSize
    );
  } catch (e) {
    // If logo fails to load, continue without it
    // eslint-disable-next-line no-console
    console.warn('Failed to load logo:', e);
  }

  // Title - smaller font
  yPosition += 10;
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 41, 55);
  doc.text('Billing Statement for Customer', margin, yPosition);

  // Billing Period - single line with smaller text in parentheses
  yPosition += 6;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(107, 114, 128);
  
  // First part: "Billing Period: January 2026 "
  const mainText = `Billing Period: ${monthYear} `;
  doc.text(mainText, margin, yPosition);
  
  // Calculate x position for the parenthetical text
  const mainTextWidth = doc.getTextWidth(mainText);
  const parentheticalX = margin + mainTextWidth;
  
  // Second part: "(January 1, 2026 - January 31, 2026)" in smaller font
  doc.setFontSize(9);
  const parentheticalText = `(${fromDate} - ${toDate})`;
  doc.text(parentheticalText, parentheticalX, yPosition);

  // Customer Info Box (no engagement tile per requirements)
  yPosition += 6;
  const billToBoxY = yPosition; // Save yPosition for "BILL TO" box
  const boxHeight = 18; // Reduced height
  const boxGap = 5; // Gap between the two boxes
  const billToBoxWidth = tableWidth * 0.65; // BILL TO box is 65% width
  const totalDueBoxWidth = tableWidth * 0.32; // TOTAL DUE box is 35% width

  // BILL TO box - 65% width on left
  const billToBoxX = margin;
  doc.setFillColor(229, 237, 255); // More prominent light blue background
  doc.roundedRect(billToBoxX, billToBoxY, billToBoxWidth, boxHeight, 3, 3, 'F');

  yPosition += 7;
  doc.setFontSize(10);
  doc.setTextColor(107, 114, 128);
  doc.text('BILL TO', billToBoxX + 5, yPosition);

  yPosition += 6;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 41, 55);
  doc.text(aggregate.customerName, billToBoxX + 5, yPosition);

  // Total Due Box - 35% width on right
  const totalDueBoxX = billToBoxX + billToBoxWidth + boxGap;
  doc.setFillColor(34, 197, 94);
  doc.roundedRect(totalDueBoxX, billToBoxY, totalDueBoxWidth, boxHeight, 3, 3, 'F');

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(255, 255, 255);
  doc.text('TOTAL DUE', totalDueBoxX + 5, billToBoxY + 6);

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(formatCurrency(aggregate.summary.finalBill || 0, currency), totalDueBoxX + 5, billToBoxY + 13);

  const fh = aggregate.fixedHours;
  const isFixedHoursCustomer = fh?.enabled === true;

  yPosition += 15;

  if (isFixedHoursCustomer && fh) {
    // Fixed Hours
    if (fh.includedRows.length > 0) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(31, 41, 55);
      doc.text('Fixed Hours', margin, yPosition);
      yPosition += 5;

      const includedBody = fh.includedRows
        .filter((r) => isResourceChargeExportRow(r.groupName, { excludeL1ForSphera: true }))
        .map((r) => [
          `${r.engagementNumber} - ${r.engagementName}`,
          r.userName,
          r.groupName,
          formatHours(r.includedHours),
          `${formatCurrency(0, currency)}/hr`,
          formatCurrency(0, currency),
        ]);
      includedBody.push([
        'Total',
        '',
        '',
        formatHours(fh.totalIncludedHours),
        '',
        formatCurrency(0, currency),
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [['Engagement', 'Resource', 'Group', 'Included Hours', 'Rate', 'Value']],
        body: includedBody,
        theme: 'striped',
        headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 8, textColor: [31, 41, 55] },
        columnStyles: getFixedHoursBreakdownColumnStyles(tableWidth),
        margin: { left: margin, right: margin, bottom: footerAreaHeight },
        tableWidth,
      });
      yPosition = (doc as any).lastAutoTable.finalY + 10;
    }

    // Overage Charges (only when overage > 0)
    if (fh.totalOverageHours > 0 && fh.overageRows.length > 0) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Overage Charges', margin, yPosition);
      yPosition += 5;

      const overageBody = fh.overageRows
        .filter((r) => isResourceChargeExportRow(r.groupName, { excludeL1ForSphera: true }))
        .map((r) => [
          `${r.engagementNumber} - ${r.engagementName}`,
          r.userName,
          r.groupName,
          formatHours(r.overageHours),
          `${formatCurrency(r.hourlyRate, currency)}/hr`,
          formatCurrency(r.billableAmount, currency),
        ]);
      overageBody.push([
        'Total',
        '',
        '',
        formatHours(fh.totalOverageHours),
        '',
        formatCurrency(fh.totalOverageAmount, currency),
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [['Engagement', 'Resource', 'Group', 'Overage Hours', 'Rate', 'Overage Charges']],
        body: overageBody,
        theme: 'striped',
        headStyles: { fillColor: [16, 185, 129], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 8, textColor: [31, 41, 55] },
        columnStyles: getFixedHoursBreakdownColumnStyles(tableWidth),
        margin: { left: margin, right: margin, bottom: footerAreaHeight },
        tableWidth,
      });
      yPosition = (doc as any).lastAutoTable.finalY + 10;
    }
  } else {
    // Resource Charges Section (export only: exclude L1 Support Engineer rows)
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(31, 41, 55);
    doc.text('Resource Charges (All Engagements)', margin, yPosition);

    yPosition += 5;

    const resources = (aggregate.resourceCharges || []).filter((r: CustomerResourceCharge) =>
      isResourceChargeExportRow(r.groupName, { excludeL1ForSphera: true })
    );

    if (resources.length > 0) {
      const totalAmount = sumAndRoundHalfUp2(
        resources.map((r) => r.billableAmount || 0)
      );

      const tableData = resources.map((r: CustomerResourceCharge) => [
        r.userName,
        r.groupName,
        formatHours(r.totalHours),
        `${formatCurrency(r.hourlyRate, currency)}/hr`,
        formatCurrency(r.billableAmount, currency),
      ]);

      tableData.push([
        'Total',
        '',
        '',
        '',
        formatCurrency(totalAmount, currency),
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [['Resource', 'Group', 'Hours', 'Rate', 'Amount']],
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [59, 130, 246],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 10,
        },
        bodyStyles: {
          fontSize: 9,
          textColor: [31, 41, 55],
        },
        columnStyles: {
          0: { halign: 'left' },
          1: { halign: 'left' },
          2: { halign: 'right' },
          3: { halign: 'right' },
          4: { halign: 'right', fontStyle: 'bold' },
        },
        margin: { left: margin, right: margin, bottom: footerAreaHeight },
        tableWidth: tableWidth,
        didParseCell: (data: any) => {
          if (data.section === 'head' && data.column.index >= 2) {
            data.cell.styles.halign = 'right';
          }
          if (data.section === 'body' && data.row.index === tableData.length - 1) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fontSize = 10;
            data.cell.styles.fillColor = [243, 244, 246];
            data.cell.styles.textColor = [31, 41, 55];
          }
        },
      });

      yPosition = (doc as any).lastAutoTable.finalY + 10;
    } else {
      yPosition += 10;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(107, 114, 128);
      doc.text('No resource charges for this period', margin, yPosition);
      yPosition += 15;
    }
  }

  if (yPosition > maxContentY) {
    doc.addPage();
    yPosition = margin;
  }

  // Complimentary Hours: logged time on service-credit engagements (bifurcated from billable resource work).
  // Only when the customer has at least one engagement with Service Credit enabled.
  const ch = aggregate.complimentaryHours;
  if (ch && ch.hasServiceCreditEngagements) {
    const chResourceRows = ch.resourceRows || [];
    if (chResourceRows.length > 0) {
      // Keep title + table together to avoid large blank page sections.
      const estimatedTableHeight = 24 + (chResourceRows.length + 1) * 8;
      ensureSpaceForFooter(estimatedTableHeight);

      yPosition += 5;
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(31, 41, 55);
      const chLabel =
        ch.engagementNames && ch.engagementNames.length > 0
          ? `Complimentary Hours (${ch.engagementNames.join(', ')})`
          : 'Complimentary Hours';
      const chTitleLines = doc.splitTextToSize(chLabel, tableWidth);
      doc.text(chTitleLines, margin, yPosition);
      yPosition += chTitleLines.length * 5;

      const totalResourceOverageAmount = sumAndRoundHalfUp2(
        chResourceRows.map((r) => Number(r.billableAmount || 0))
      );
      const resourceTableBody = chResourceRows.map((r) => [
        r.userName,
        r.groupName,
        formatHours(r.totalHours),
        `${formatCurrency(r.hourlyRate, currency)}/hr`,
        formatCurrency(r.billableAmount || 0, currency),
      ]);
      resourceTableBody.push([
        'Total',
        '',
        formatHours(ch.totalComplimentaryHours || 0),
        '',
        formatCurrency(totalResourceOverageAmount, currency),
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [['Resource', 'Group', 'Hours', 'Rate', 'Amount']],
        body: resourceTableBody,
        theme: 'striped',
        headStyles: {
          fillColor: [16, 185, 129],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 10,
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [31, 41, 55],
        },
        columnStyles: {
          0: { halign: 'left' },
          1: { halign: 'left' },
          2: { halign: 'right' },
          3: { halign: 'right' },
          4: { halign: 'right', fontStyle: 'bold' },
        },
        margin: { left: margin, right: margin, bottom: footerAreaHeight },
        tableWidth,
        didParseCell: (data: any) => {
          if (data.section === 'head' && data.column.index >= 2) {
            data.cell.styles.halign = 'right';
          }
          if (data.section === 'body' && data.row.index === resourceTableBody.length - 1) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [236, 253, 245];
          }
        },
      });

      yPosition = (doc as any).lastAutoTable.finalY + 10;
    }

    if (yPosition > maxContentY) {
      doc.addPage();
      yPosition = margin;
    }
  }

  const patchingLinesForPdf = (aggregate.patchingServices || []).filter(
    (line) => line.showInPeriod !== false
  );
  if (patchingLinesForPdf.length > 0) {
    yPosition = renderPatchingPdfSections({
      doc,
      lines: patchingLinesForPdf,
      currency,
      margin,
      tableWidth,
      footerAreaHeight,
      maxContentY,
      startY: yPosition
    });
  }

  // Service Plans Section (if applicable)
  if (aggregate.serviceFlatFeeDetails && aggregate.serviceFlatFeeDetails.length > 0) {
    yPosition += 5;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(31, 41, 55);
    doc.text('Service Plans', margin, yPosition);

    yPosition += 5;

    const planTableData = aggregate.serviceFlatFeeDetails.map((plan) => [
      plan.planName,
      formatCurrency(plan.price, currency),
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [['Plan', 'Amount']],
      body: planTableData,
      theme: 'striped',
      headStyles: {
        fillColor: [139, 92, 246], // Purple
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 10,
      },
      bodyStyles: {
        fontSize: 9,
        textColor: [31, 41, 55],
      },
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'right', fontStyle: 'bold' },
      },
      margin: { left: margin, right: margin, bottom: footerAreaHeight },
      tableWidth: tableWidth, // Use 100% of available width
      didParseCell: (data: any) => {
        // Right-align header cell for Amount column
        if (data.section === 'head' && data.column.index === 1) {
          data.cell.styles.halign = 'right';
        }
      },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;
    // Ensure yPosition doesn't extend into footer area
    if (yPosition > maxContentY) {
      doc.addPage();
      yPosition = margin;
    }
  }

  // Billing Summary Section
  yPosition += 5;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 41, 55);
  doc.text('Customer Billing Summary', margin, yPosition);

  yPosition += 5;

  const summaryRows: (string | number)[][] = [];

  if (isFixedHoursCustomer && fh) {
    const servicePlanFee = aggregate.summary.servicePlanFee || 0;
    if (servicePlanFee > 0) {
      summaryRows.push([
        'Service Fee (Minimum Fee Adjustment)',
        '',
        formatCurrency(servicePlanFee, currency),
      ]);
    }

    if (aggregate.summary.totalPlatformFee > 0) {
      summaryRows.push([
        'Platform Cost',
        '',
        formatCurrency(aggregate.summary.totalPlatformFee, currency),
      ]);
    }

    if (aggregate.summary.totalServiceFlatFee > 0) {
      summaryRows.push([
        'Service Plans',
        '',
        formatCurrency(aggregate.summary.totalServiceFlatFee, currency),
      ]);
    }

    if ((aggregate.summary.totalPatchingAmount || 0) > 0) {
      summaryRows.push([
        'Patching Services (Resource Costs)',
        '',
        formatCurrency(aggregate.summary.totalPatchingAmount, currency),
      ]);
    }

    if ((fh.totalOverageAmount || 0) > 0) {
      summaryRows.push([
        'Overage Charges',
        '',
        formatCurrency(fh.totalOverageAmount, currency),
      ]);
    }

    summaryRows.push([
      'Total',
      '',
      formatCurrency(aggregate.summary.finalBill || 0, currency),
    ]);
  } else {
  // Calculate minimum fee adjustment (Service Fee)
  const serviceFeeAdjustment = Math.max(
    (aggregate.summary.servicePlanFee || 0) - (aggregate.summary.totalResourceCharges || 0),
    0
  );

  // Customer Billing Summary: no Total Hours in export; keep Total Resource Amount only
  summaryRows.push([
    'Resource Charges',
    '',
    formatCurrency(aggregate.summary.totalResourceCharges, currency),
  ]);

  if (serviceFeeAdjustment > 0) {
    summaryRows.push([
      'Service Fee (Minimum Fee Adjustment)',
      '',
      formatCurrency(serviceFeeAdjustment, currency),
    ]);
  }

  if (aggregate.summary.totalPlatformFee > 0) {
    summaryRows.push([
      'Platform Cost',
      '',
      formatCurrency(aggregate.summary.totalPlatformFee, currency),
    ]);
  }

  if ((aggregate.summary.totalOverageAmount || 0) > 0) {
    summaryRows.push([
      'Overage Charges (Service Credit)',
      '',
      formatCurrency(aggregate.summary.totalOverageAmount, currency),
    ]);
  }

  if (aggregate.summary.totalServiceFlatFee > 0) {
    summaryRows.push([
      'Service Plans',
      '',
      formatCurrency(aggregate.summary.totalServiceFlatFee, currency),
    ]);
  }

  if ((aggregate.summary.totalPatchingAmount || 0) > 0) {
    summaryRows.push([
      'Patching Services (Resource Costs)',
      '',
      formatCurrency(aggregate.summary.totalPatchingAmount, currency),
    ]);
  }

  // Add Total row
  summaryRows.push([
    'Total',
    '',
    formatCurrency(aggregate.summary.finalBill || 0, currency),
  ]);
  }

  autoTable(doc, {
    startY: yPosition,
    head: [['Description', '', 'Amount']],
    body: summaryRows,
    theme: 'plain',
    headStyles: {
      fillColor: [243, 244, 246],
      textColor: [107, 114, 128],
      fontStyle: 'bold',
      fontSize: 9,
    },
    bodyStyles: {
      fontSize: 10,
      textColor: [31, 41, 55],
    },
    columnStyles: {
      0: { halign: 'left' }, // Description
      1: { halign: 'right' }, // Details
      2: { halign: 'right' }, // Amount
    },
    margin: { left: margin, right: margin, bottom: footerAreaHeight },
    tableWidth: tableWidth, // Use 100% of available width
    didParseCell: (data: any) => {
      // Right-align header cells for Details and Amount columns
      if (data.section === 'head' && data.column.index >= 1) {
        data.cell.styles.halign = 'right';
      }
      // Highlight the last row (Total) with background color and make it bold
      if (data.section === 'body' && data.row.index === summaryRows.length - 1) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fontSize = 11;
        data.cell.styles.fillColor = [229, 237, 255]; // Light blue-gray background (matches BILL TO box)
        data.cell.styles.textColor = [31, 41, 55]; // Dark text for contrast
      }
    },
  });

  // Ensure content doesn't overlap with footer - check after last table
  // autoTable should handle page breaks automatically with bottom margin
  // The footer will be added to all pages, so content should not overlap

  // Footer - add to all pages in the reserved footer area
  const totalPages = (doc as any).internal.pages.length - 1; // jsPDF pages array is 0-indexed, subtract 1 for actual count
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    
    // Footer background - 100% width
    const footerStartY = pageHeight - footerAreaHeight;
    doc.setFillColor(249, 250, 251); // Light gray background
    doc.rect(0, footerStartY, pageWidth, footerAreaHeight, 'F');
    
    // Position footer in the reserved footer area (30 units from bottom)
    const currentFooterY = pageHeight - footerAreaHeight + 10; // Start footer text 10 units into the footer area
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(156, 163, 175);
    
    // First footer line
    doc.text('This is a system-generated customer billing statement from Tenarai Pulse.', margin, currentFooterY);
    
    // Second footer line (generated date)
    doc.text(
      `Generated on ${new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })}`,
      margin,
      currentFooterY + 5
    );
    
    // Page number on the right
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 20, currentFooterY + 8);
  }

  // Save the PDF
  const filename = formatCustomerBillingFilename(aggregate.customerName, aggregate.billingPeriod, 'pdf');
  doc.save(filename);
};

export default generateCustomerBillingPdf;


